import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { Agent } from '@propertypal/shared/src/types/agents';
import { Contact, Property } from '@propertypal/shared/src/types/property';
import trackEvents from '@propertypal/shared/src/utils/trackEvents';
import CallAgentModal from '@propertypal/web-ui/src/modal/CallAgentModal';
import React, { useState } from 'react';

const EVENT_PAGE_MAP: { [key: string]: string | undefined } = {
  SRP: 'SEARCH_RESULTS',
  PDP: 'PROPERTY_DETAILS',
  DDP: 'DEVELOPMENT_DETAILS',
  'Find an Agent': 'FIND_AN_AGENT',
  'User Enquiries': undefined,
  'Agent Landing Page': 'AGENT_LANDING',
};

const useCallAgent = (onCloseModal?: () => void) => {
  const [modal, setModal] = useState<React.ReactElement | undefined>();
  const [phoneNumber, setPhoneNumber] = useState('');

  const onClose = () => {
    setModal(undefined);
    if (onCloseModal) onCloseModal();
  };

  const trackCall = (property: Property, accountNumberSelected: string, page: string) => {
    trackEvents({
      eventType: 'PHONE_REVEAL',
      property: property.type === 'property' ? property.id : undefined,
      development: property.type === 'development' ? property.id : undefined,
      eventPageType: EVENT_PAGE_MAP[page],
      accountNumber: accountNumberSelected,
    });
  };

  const trackAgentCall = (agent: Agent, page: string, property?: Property) => {
    trackEvents({
      eventType: 'PHONE_REVEAL',
      eventPageType: EVENT_PAGE_MAP[page],
      accountNumber: agent.accountNumber,
      property: property?.type === 'property' ? property.id : undefined,
      development: property?.type === 'development' ? property.id : undefined,
    });
  };

  const revealAgentNumber = async (agent: Agent, page: string) => {
    if (agent.officePhone?.displayNumber) {
      setPhoneNumber(agent.officePhone.displayNumber);
      trackAgentCall(agent, page);
    }
  };

  // Used on mobile where calls can be made directly via phone using window.open
  const callAgent = async (property?: Property, contact?: Contact, agent?: Agent, page?: string, location?: string) => {
    trackGaEvent('phone_reveal', {
      page,
      location,
      account_number: agent?.accountNumber || contact?.accountNumber || property?.account.accountNumber,
    });

    if (agent) {
      setModal(undefined);
      if (property && page) trackCall(property, agent?.accountNumber, page);
      if (agent.officePhone?.displayNumber) window.open(`tel:${agent.officePhone.displayNumber}`, '_self');
    } else if (contact?.phoneNumber) {
      setModal(undefined);
      if (property && page) trackCall(property, contact.accountNumber, page);
      window.open(`tel:${contact.phoneNumber.displayNumber}`, '_self');
    } else if (property) {
      const { contacts } = property;

      if (contacts) {
        if (contacts?.all.length > 1) {
          const onSelected = (organisation: string) => {
            // find account number
            const account = contacts.all.find((a) => a.organisation === organisation);

            if (account) {
              if (page) trackCall(property, account.accountNumber, page);
              // open telephone link
              window.open(`tel:${account?.phoneNumber?.displayNumber}`, '_self');
            }
          };

          setModal(<CallAgentModal show onClose={onClose} contacts={contacts.all} onSelected={onSelected} />);
        } else {
          setModal(undefined);
          const a = contacts.default || contacts.all[0];

          if (a.phoneNumber) {
            if (page) trackCall(property, a.accountNumber, page);
            window.open(`tel:${a.phoneNumber.displayNumber}`, '_self');
          }
        }
      }
    }
  };

  // Used on desktop where phone number is displayed on screen
  const showPhoneModal = (
    property?: Property,
    contact?: Contact,
    agent?: Agent,
    page?: string,
    location?: string,
    noModal?: boolean,
    accountNumberSelected?: string,
  ) => {
    trackGaEvent('phone_reveal', {
      location,
      page,
      account_number: agent?.accountNumber || contact?.accountNumber || property?.account.accountNumber,
    });

    if (agent) {
      if (page) trackAgentCall(agent, page, property);

      if (!noModal) setModal(<CallAgentModal show onClose={onClose} contacts={[agent]} property={property} />);
    } else if (contact?.phoneNumber) {
      if (property && page) trackCall(property, contact.accountNumber, page);

      if (!noModal) setModal(<CallAgentModal show onClose={onClose} contacts={[contact]} property={property} />);
    } else if (property?.contacts) {
      const contacts = accountNumberSelected
        ? property.contacts.all.filter((item) => item.accountNumber === accountNumberSelected)
        : property.contacts.all;
      const contactsForModal = contacts.length > 0 ? contacts : property.contacts.all;

      if (page) trackCall(property, accountNumberSelected || property.account.accountNumber, page);

      if (!noModal) setModal(<CallAgentModal show onClose={onClose} contacts={contactsForModal} property={property} />);
    } else if (property?.agents) {
      if (page) trackCall(property, property.account.accountNumber, page);

      if (!noModal) setModal(<CallAgentModal show onClose={onClose} contacts={property.agents} property={property} />);
    }
  };

  return {
    callAgent,
    modal,
    trackCall,
    showPhoneModal,
    revealAgentNumber,
    phoneNumber,
    setPhoneNumber,
  };
};

export default useCallAgent;
